import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Copy, Loader2, Send, Sparkles, TrendingUp } from "lucide-react";

import { plansApi } from "@/api/plans";
import { type DayOptimum, optimizerApi } from "@/api/optimizer";
import { clientsApi } from "@/api/clients";
import { parametersApi } from "@/api/parameters";
import { useCanEdit } from "@/hooks/useRole";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { todayIso } from "@/lib/format";

interface RowDraft {
  power_kw: string;
  volumes: Record<string, string>;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);

export function PlanPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canEdit = useCanEdit();

  const [planDate, setPlanDate] = useState(todayIso());

  const planQ = useQuery({
    queryKey: ["plans", planDate],
    queryFn: () => plansApi.get(planDate),
  });
  const optQ = useQuery({
    queryKey: ["optimizer", planDate],
    queryFn: () => optimizerApi.day(planDate),
    // не делаем refetchInterval: цены РДН достаточно медленно меняются
    retry: 0,
  });
  const clientsQ = useQuery({
    queryKey: ["clients", { includeInactive: false }],
    queryFn: () => clientsApi.list(false),
  });
  const kguQ = useQuery({
    queryKey: ["kgu-params", planDate],
    queryFn: () => parametersApi.getKguParams(planDate),
    retry: 0,
  });
  const powerMin = Number(kguQ.data?.power_min_kw ?? 750);
  const powerMax = Number(kguQ.data?.power_max_kw ?? 1500);

  const submitted = !!planQ.data?.submitted_at;
  const readonly = !canEdit || submitted;

  const electricityClients = useMemo(
    () => (clientsQ.data ?? []).filter((c) => c.is_active),
    [clientsQ.data],
  );

  // Local-state редактирования: rows[hour] = draft. Подтягиваем из plan при загрузке.
  const [rows, setRows] = useState<Record<number, RowDraft>>({});

  useEffect(() => {
    if (!planQ.data) {
      setRows({});
      return;
    }
    const next: Record<number, RowDraft> = {};
    for (const h of planQ.data.hours) {
      next[h.hour] = {
        power_kw: String(h.power_kw),
        volumes: Object.fromEntries(
          Object.entries(h.volumes).map(([k, v]) => [k, String(v)]),
        ),
      };
    }
    setRows(next);
  }, [planQ.data]);

  const setHour = (hour: number, patch: Partial<RowDraft>) => {
    setRows((s) => ({ ...s, [hour]: { ...(s[hour] ?? { power_kw: "", volumes: {} }), ...patch } }));
  };
  const setHourVolume = (hour: number, code: string, value: string) => {
    setRows((s) => {
      const cur = s[hour] ?? { power_kw: "", volumes: {} };
      return { ...s, [hour]: { ...cur, volumes: { ...cur.volumes, [code]: value } } };
    });
  };
  const setHourPower = (hour: number, value: string) => setHour(hour, { power_kw: value });

  const saveHour = useMutation({
    mutationFn: ({ hour, draft }: { hour: number; draft: RowDraft }) =>
      plansApi.putHour(planDate, hour, {
        power_kw: Number(draft.power_kw || "0"),
        volumes: Object.fromEntries(
          Object.entries(draft.volumes).map(([k, v]) => [k, Number(v || "0")]),
        ),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["plans", planDate] }),
  });

  const copyMutation = useMutation({
    mutationFn: (source: "yesterday" | "weekly_avg") => plansApi.copyFrom(planDate, source),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["plans", planDate] }),
  });

  const submitMutation = useMutation({
    mutationFn: () => plansApi.submit(planDate),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["plans", planDate] }),
  });

  const applyRecommendations = () => {
    if (!optQ.data) return;
    const next: Record<number, RowDraft> = { ...rows };
    for (const h of optQ.data.hours) {
      next[h.hour] = {
        power_kw: String(Math.round(h.best.power_kw)),
        volumes: Object.fromEntries(
          Object.entries(h.best.volumes_kwh).map(([k, v]) => [k, String(Math.round(v))]),
        ),
      };
    }
    setRows(next);
  };

  const errorBanner = (() => {
    const errs: AxiosError<{ error?: { code?: string } }> | undefined =
      (saveHour.error as AxiosError<{ error?: { code?: string } }> | undefined)
      ?? (copyMutation.error as AxiosError<{ error?: { code?: string } }> | undefined)
      ?? (submitMutation.error as AxiosError<{ error?: { code?: string } }> | undefined);
    const code = errs?.response?.data?.error?.code;
    if (!code) return null;
    if (code in (t("plan.errors", { returnObjects: true }) as Record<string, string>)) {
      return t(`plan.errors.${code}`);
    }
    if (code === "no_yesterday_plan" || code === "no_weekly_data") {
      return t(`plan.errors.${code}`);
    }
    return code;
  })();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-brand-navy-700">{t("plan.title")}</h1>
        <p className="text-sm text-slate-500">{t("plan.subtitle")}</p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Label className="text-sm">{t("plan.date")}:</Label>
            <Input
              type="date"
              value={planDate}
              onChange={(e) => setPlanDate(e.target.value)}
              className="w-44"
            />
          </div>
          {canEdit && !submitted && (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm"
                onClick={() => copyMutation.mutate("yesterday")}
                disabled={copyMutation.isPending}>
                <Copy className="h-4 w-4" /> {t("plan.copy_yesterday")}
              </Button>
              <Button variant="outline" size="sm"
                onClick={() => copyMutation.mutate("weekly_avg")}
                disabled={copyMutation.isPending}>
                <Copy className="h-4 w-4" /> {t("plan.copy_weekly_avg")}
              </Button>
              <Button variant="outline" size="sm"
                onClick={applyRecommendations}
                disabled={!optQ.data}>
                <Sparkles className="h-4 w-4" /> {t("plan.apply_recommendations")}
              </Button>
            </div>
          )}
          {canEdit && (
            <Button
              variant={submitted ? "outline" : "default"}
              disabled={submitted || submitMutation.isPending}
              onClick={() => {
                if (window.confirm(t("plan.submit_confirm"))) submitMutation.mutate();
              }}
            >
              <Send className="h-4 w-4" /> {t("plan.submit")}
            </Button>
          )}
        </CardHeader>
      </Card>

      {/* Banner: submitted / period closed */}
      {submitted && (
        <div className="rounded-md bg-brand-mint-50 border border-brand-mint-200 px-4 py-2 text-sm text-brand-mint-700">
          {t("plan.submitted_banner", {
            time: planQ.data?.submitted_at
              ? new Date(planQ.data.submitted_at).toLocaleString("uk-UA")
              : "",
          })}
        </div>
      )}

      {errorBanner && (
        <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {errorBanner}
        </div>
      )}

      {planQ.data?.last_updated_at && !submitted && (
        <div className="text-xs text-slate-500">
          {t("plan.last_updated", {
            time: new Date(planQ.data.last_updated_at).toLocaleString("uk-UA", {
              timeZone: "Europe/Kyiv",
            }),
          })}
        </div>
      )}

      {/* Hours table */}
      {planQ.isLoading ? (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> {t("common.loading")}
        </div>
      ) : (
        <Card>
          <CardContent className="overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 bg-slate-50 sticky top-0">
                  <th className="p-2 w-14">{t("common.hour")}</th>
                  <th className="p-2 w-44">{t("plan.power_kw")}</th>
                  {electricityClients.map((c) => (
                    <th key={c.id} className="p-2 w-32">
                      {c.name}
                    </th>
                  ))}
                  <th className="p-2 w-32 text-brand-blue-600">{t("plan.rec_power")}</th>
                  {!readonly && <th className="p-2 w-20" />}
                </tr>
              </thead>
              <tbody>
                {HOURS.map((hour) => (
                  <PlanRow
                    key={hour}
                    hour={hour}
                    draft={rows[hour]}
                    clients={electricityClients}
                    optimum={optQ.data}
                    readonly={readonly}
                    powerMin={powerMin}
                    powerMax={powerMax}
                    onPowerChange={(v) => setHourPower(hour, v)}
                    onVolumeChange={(code, v) => setHourVolume(hour, code, v)}
                    onSave={() => {
                      const d = rows[hour];
                      if (!d) return;
                      saveHour.mutate({ hour, draft: d });
                    }}
                    saving={saveHour.isPending && saveHour.variables?.hour === hour}
                  />
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Optimizer summary */}
      {optQ.data && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <TrendingUp className="h-4 w-4 text-brand-mint-600" />
              {t("optimizer.total_optimal")}:{" "}
              {optQ.data.total_optimal_profit_uah.toLocaleString("uk-UA", { maximumFractionDigits: 0 })} грн
            </CardTitle>
            <CardDescription>{t("plan.errors.rdn_unavailable")}</CardDescription>
          </CardHeader>
        </Card>
      )}
    </div>
  );
}

// ---------- Row ----------

interface PlanRowProps {
  hour: number;
  draft: RowDraft | undefined;
  clients: Awaited<ReturnType<typeof clientsApi.list>>;
  optimum: DayOptimum | undefined;
  readonly: boolean;
  powerMin: number;
  powerMax: number;
  onPowerChange: (v: string) => void;
  onVolumeChange: (code: string, v: string) => void;
  onSave: () => void;
  saving: boolean;
}

function PlanRow({
  hour, draft, clients, optimum, readonly, powerMin, powerMax,
  onPowerChange, onVolumeChange, onSave, saving,
}: PlanRowProps) {
  const opt = optimum?.hours.find((h) => h.hour === hour);
  const powerNum = Number(draft?.power_kw ?? 0);
  const isOutOfRange =
    draft?.power_kw !== "" &&
    draft?.power_kw !== undefined &&
    (powerNum < powerMin || powerNum > powerMax);
  const presetValues = [
    Math.round(powerMin), // 50%-equivalent: power_min itself
    Math.round((powerMin + powerMax) / 2),
    Math.round(powerMax),
  ];

  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50/40">
      <td className="p-2 font-mono text-xs text-slate-600">
        {hour.toString().padStart(2, "0")}:00
      </td>
      <td className="p-2">
        <div className="flex flex-col gap-1 w-44">
          <Input
            type="number"
            step="10"
            min={0}
            value={draft?.power_kw ?? ""}
            onChange={(e) => onPowerChange(e.target.value)}
            disabled={readonly}
            className={cn("w-32", isOutOfRange && "border-red-400 bg-red-50")}
            title={isOutOfRange ? `Допустимо ${powerMin}–${powerMax} кВт` : ""}
          />
          {!readonly && (
            <>
              <input
                type="range"
                min={powerMin}
                max={powerMax}
                step={Math.max(1, Math.round((powerMax - powerMin) / 50))}
                value={Math.min(powerMax, Math.max(powerMin, powerNum || powerMin))}
                onChange={(e) => onPowerChange(e.target.value)}
                className="w-full accent-emerald-600 cursor-pointer"
                title={`${powerMin}–${powerMax} кВт`}
              />
              <div className="flex gap-1">
                {presetValues.map((v, i) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => onPowerChange(String(v))}
                    className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 hover:bg-slate-100"
                    title={`${["min", "mid", "max"][i]} = ${v} кВт`}
                  >
                    {["min", "mid", "max"][i]}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </td>
      {clients.map((c) => (
        <td key={c.id} className="p-2">
          <Input
            type="number"
            step="1"
            min={0}
            value={draft?.volumes[c.code] ?? ""}
            onChange={(e) => onVolumeChange(c.code, e.target.value)}
            disabled={readonly}
            className="w-28"
          />
        </td>
      ))}
      <td className="p-2 text-xs text-brand-blue-600">
        {opt ? (
          <div className="flex flex-col">
            <span className="font-medium">{Math.round(opt.best.power_kw)} кВт</span>
            <span className="text-slate-500">
              {opt.best.profit_uah > 0 ? "+" : ""}
              {Math.round(opt.best.profit_uah).toLocaleString("uk-UA")} грн
            </span>
          </div>
        ) : (
          "—"
        )}
      </td>
      {!readonly && (
        <td className="p-2">
          <Button size="sm" variant="outline" onClick={onSave} disabled={saving}>
            <span className={cn("h-3 w-3 rounded-full", saving ? "bg-amber-400 animate-pulse" : "bg-slate-300")} />
          </Button>
        </td>
      )}
    </tr>
  );
}
